import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Shield,
  AlertTriangle,
  Clock,
  Key,
  Save,
  Loader2,
  RefreshCw,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  SkipForward,
  Trash2,
  Download,
  Zap,
  Phone,
  Lock,
  Eye,
  EyeOff,
  User,
  DollarSign,
  Ban,
  Play,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  success: { label: "Success", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", icon: CheckCircle },
  failed: { label: "Failed", color: "bg-red-500/10 text-red-500 border-red-500/20", icon: XCircle },
  pending: { label: "Pending", color: "bg-amber-500/10 text-amber-500 border-amber-500/20", icon: Clock },
  skipped: { label: "Skipped", color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: SkipForward },
  abandoned: { label: "Stopped", color: "bg-slate-500/20 text-slate-400 border-slate-500/30", icon: Ban },
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncStats, setSyncStats] = useState({ total: 0, success: 0, failed: 0, pending: 0, skipped: 0 });
  const [ringbaStatus, setRingbaStatus] = useState(null);
  const [autoImportStatus, setAutoImportStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [retrying, setRetrying] = useState(false);
  const [importing, setImporting] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [togglingAutoImport, setTogglingAutoImport] = useState(false);
  
  // Confirm dialog state (replaces browser window.confirm which Safari can silently block)
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Shape: { title, description, confirmLabel, confirmVariant, onConfirm }
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSyncData();
  }, []);

  useEffect(() => {
    fetchSyncLogs();
  }, [statusFilter]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncData = async () => {
    try {
      const [statsRes, statusRes, autoImportRes] = await Promise.all([
        axios.get(`${API}/sync-logs/stats`),
        axios.get(`${API}/ringba/status`),
        axios.get(`${API}/auto-import/status`).catch(() => ({ data: null })),
      ]);
      setSyncStats(statsRes.data);
      setRingbaStatus(statusRes.data);
      setAutoImportStatus(autoImportRes.data);
    } catch (error) {
      console.error("Failed to fetch sync data:", error);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      setSyncLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/sync-logs?${params}`);
      setSyncLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch sync logs:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleToggleAutoImport = async (enabled) => {
    setTogglingAutoImport(true);
    try {
      const formData = new FormData();
      formData.append("enabled", enabled.toString());
      formData.append("interval_minutes", (settings.auto_import_interval_minutes || 60).toString());
      
      await axios.post(`${API}/auto-import/toggle`, formData);
      setSettings({ ...settings, auto_import_enabled: enabled });
      toast.success(`Auto-import ${enabled ? 'enabled' : 'disabled'}`);
      fetchSyncData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle auto-import");
    } finally {
      setTogglingAutoImport(false);
    }
  };

  const handleTriggerAutoImport = async () => {
    try {
      await axios.post(`${API}/auto-import/trigger`);
      toast.success("Auto-import triggered");
      fetchSyncData();
      setTimeout(() => {
        fetchSyncLogs();
        fetchSyncData();
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger import");
    }
  };

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/sync-logs/retry-failed`);
      toast.success("Retry process initiated");
      setTimeout(() => {
        fetchSyncLogs();
        fetchSyncData();
      }, 2000);
    } catch (error) {
      toast.error("Failed to retry syncs");
    } finally {
      setRetrying(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/sync-logs/${logId}`);
      toast.success("Log deleted");
      fetchSyncLogs();
      fetchSyncData();
    } catch (error) {
      toast.error("Failed to delete log");
    }
  };

  const handleStopLog = (logId) => {
    setConfirmDialog({
      title: "Stop retrying this action forever?",
      description: "This marks it as abandoned and it will NEVER be retried automatically. Use if a specific action is stuck in a loop.",
      confirmLabel: "Stop Forever",
      confirmVariant: "destructive",
      onConfirm: async () => {
        try {
          await axios.post(`${API}/sync-logs/${logId}/abandon`);
          toast.success("Stopped — will not retry");
          fetchSyncLogs();
          fetchSyncData();
        } catch (error) {
          const msg = error?.response?.data?.detail || "Failed to stop sync";
          toast.error(msg);
        }
      },
    });
  };

  const handleResumeLog = async (logId) => {
    try {
      await axios.post(`${API}/sync-logs/${logId}/resume`);
      toast.success("Resumed — will retry on next cycle");
      fetchSyncLogs();
      fetchSyncData();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to resume sync";
      toast.error(msg);
    }
  };

  const [purging, setPurging] = useState(false);
  const [resetting, setResetting] = useState(false);
  const handlePurgeBadLogs = () => {
    setConfirmDialog({
      title: "Purge ALL bad sync logs?",
      description: "This deletes all sync_logs matching known bad patterns (\"not configured\", MongoDB Atlas timeouts, abandoned pending entries). This cannot be undone.",
      confirmLabel: "Purge",
      confirmVariant: "destructive",
      onConfirm: async () => {
        setPurging(true);
        try {
          const response = await axios.post(`${API}/sync-logs/cleanup`);
          const { total_deleted, breakdown } = response.data || {};
          const nc = breakdown?.not_configured || 0;
          const timeouts = (breakdown?.mongo_atlas_timeout || 0) + (breakdown?.read_operation_timeout || 0);
          const abandoned = breakdown?.abandoned || 0;
          const stale = breakdown?.stale_empty_object_id || 0;
          toast.success(`Purged ${total_deleted || 0} bad logs (${nc} not-configured, ${timeouts} timeouts, ${abandoned} abandoned, ${stale} stale)`);
          fetchSyncLogs();
          fetchSyncData();
        } catch (error) {
          const msg = error?.response?.data?.detail || error?.message || "Purge failed";
          const code = error?.response?.status;
          toast.error(`Purge failed${code ? ` (${code})` : ""}: ${msg}`);
          console.error("Purge Bad Logs error:", error);
        } finally {
          setPurging(false);
        }
      },
    });
  };

  const handleFactoryReset = () => {
    setConfirmDialog({
      title: "FACTORY RESET auto-import?",
      description: "This will: delete ALL import_attempts tracking, purge all failed sync_logs, reset last_auto_import to NULL (fresh 7-day lookback), restart the background import loop, re-register all scheduler jobs, and immediately fetch fresh from Ringba. Use this when auto-import is broken and you want a clean slate.",
      confirmLabel: "Factory Reset",
      confirmVariant: "destructive",
      onConfirm: async () => {
        setResetting(true);
        try {
          const response = await axios.post(`${API}/auto-import/factory-reset`);
          const r = response.data.report;
          toast.success(
            `Reset complete: ${r.new_calls_imported} new calls imported in ${r.duration_sec}s (purged ${r.import_attempts_deleted} tracking rows + ${r.bad_sync_logs_deleted} bad logs)`,
            { duration: 10_000 }
          );
          fetchSyncLogs();
          fetchSyncData();
        } catch (error) {
          toast.error(error?.response?.data?.detail || "Factory reset failed");
        } finally {
          setResetting(false);
        }
      },
    });
  };

  const [purgeNumberInput, setPurgeNumberInput] = useState("");
  const [purgingNumber, setPurgingNumber] = useState(false);
  const handlePurgeNumber = () => {
    const raw = (purgeNumberInput || "").trim();
    if (!raw) {
      toast.error("Enter a phone number first");
      return;
    }
    const digits = raw.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Invalid phone number");
      return;
    }
    const plusForm = "+" + digits;
    setConfirmDialog({
      title: `Permanently block ${plusForm}?`,
      description: `This will: delete ALL sync_logs for this number (any status), delete all import_attempts for this number, add it to your local block list, and mark existing calls as auto-blocked. Future calls from ${plusForm} will be silently ignored by auto-import — even if Ringba sync fails. Use this when Ringba block sync keeps failing and the same number keeps looping.`,
      confirmLabel: "Purge Forever",
      confirmVariant: "destructive",
      onConfirm: async () => {
        setPurgingNumber(true);
        try {
          const response = await axios.post(`${API}/callers/purge-number`, { caller_number: raw });
          const d = response.data.deleted || {};
          toast.success(
            `${response.data.caller_number} purged — sync_logs:${d.sync_logs || 0}, import_attempts:${d.import_attempts || 0}, calls_blocked:${response.data.calls_marked_blocked || 0}`,
            { duration: 8_000 }
          );
          setPurgeNumberInput("");
          fetchSyncLogs();
          fetchSyncData();
        } catch (error) {
          toast.error(error?.response?.data?.detail || "Failed to purge number");
        } finally {
          setPurgingNumber(false);
        }
      },
    });
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.get(`${API}/ringba/test-connection`);
      toast.success(response.data.message || "Connection successful!");
      fetchSyncData();
      fetchSyncLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection failed");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleImportCalls = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/ringba/import-calls?days_back=7&limit=100`);
      toast.success(`Import complete: ${response.data.imported} calls imported, ${response.data.analysis_queued} queued for analysis`);
      fetchSyncData();
      fetchSyncLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleImportTodaysCalls = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/ringba/import-today?limit=100`);
      const data = response.data;
      if (data.imported > 0) {
        toast.success(`Live import complete: ${data.imported} new calls from today, ${data.analysis_queued} queued for analysis`);
      } else if (data.total_fetched === 0) {
        toast.info("No new calls found for today yet. Make a test call to your Ringba tracking number.");
      } else {
        toast.info(`${data.skipped} calls already imported, ${data.total_fetched} total found today`);
      }
      fetchSyncData();
      fetchSyncLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const formData = new FormData();
      
      if (settings.spam_keywords?.length) {
        formData.append("spam_keywords", settings.spam_keywords.join(","));
      }
      if (settings.adult_keywords?.length) {
        formData.append("adult_keywords", settings.adult_keywords.join(","));
      }
      formData.append("retry_limit", settings.retry_limit.toString());
      formData.append("retry_window_minutes", settings.retry_window_minutes.toString());
      formData.append("block_duration_minutes", settings.block_duration_minutes.toString());
      formData.append("publisher_pause_threshold", settings.publisher_pause_threshold.toString());
      formData.append("adult_block_threshold", settings.adult_block_threshold.toString());
      formData.append("spam_block_threshold", settings.spam_block_threshold.toString());
      formData.append("buffer_time_after_ivr", settings.buffer_time_after_ivr.toString());
      
      if (settings.ringba_api_key) {
        formData.append("ringba_api_key", settings.ringba_api_key);
      }
      if (settings.ringba_account_id) {
        formData.append("ringba_account_id", settings.ringba_account_id);
      }
      if (settings.ringba_webhook_url) {
        formData.append("ringba_webhook_url", settings.ringba_webhook_url);
      }
      if (settings.ringba_webhook_secret) {
        formData.append("ringba_webhook_secret", settings.ringba_webhook_secret);
      }
      formData.append("ringba_enabled", settings.ringba_enabled.toString());
      
      // Auto-import settings
      formData.append("auto_import_enabled", (settings.auto_import_enabled || false).toString());
      formData.append("auto_import_interval_minutes", (settings.auto_import_interval_minutes || 60).toString());
      if (settings.low_minutes_threshold !== undefined) {
        formData.append("low_minutes_threshold", settings.low_minutes_threshold.toString());
      }

      await axios.put(`${API}/settings`, formData);
      toast.success("Settings saved successfully");
      fetchSyncData();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters with uppercase, lowercase, number, and special character");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Failed to change password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load settings. Please refresh the page.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Configure fraud detection rules and Ringba integration
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchSettings(); fetchSyncData(); }} data-testid="refresh-settings">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-settings"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="detection" className="space-y-6">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="detection">
            <Shield className="h-4 w-4 mr-2" />
            Detection
          </TabsTrigger>
          <TabsTrigger value="rules">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="ringba">
            <Key className="h-4 w-4 mr-2" />
            Ringba
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="billing-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="sync-log">
            <Activity className="h-4 w-4 mr-2" />
            Sync Log
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="account-tab">
            <User className="h-4 w-4 mr-2" />
            Account
          </TabsTrigger>
        </TabsList>

        {/* Detection Settings */}
        <TabsContent value="detection" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Spam Keywords
              </CardTitle>
              <CardDescription>
                Keywords that will flag a call as spam when detected in the transcript
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="spam-keywords">Keywords (comma-separated)</Label>
                <Input
                  id="spam-keywords"
                  placeholder="body massage, escort, spa service..."
                  value={settings.spam_keywords?.join(", ") || ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      spam_keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean),
                    })
                  }
                  data-testid="spam-keywords-input"
                />
                <p className="text-xs text-muted-foreground">
                  Current: {settings.spam_keywords?.length || 0} keywords
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-red-500" />
                Adult Keywords
              </CardTitle>
              <CardDescription>
                Keywords that will flag a call as adult content
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="adult-keywords">Keywords (comma-separated)</Label>
                <Input
                  id="adult-keywords"
                  placeholder="escort, sex, adult services..."
                  value={settings.adult_keywords?.join(", ") || ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      adult_keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean),
                    })
                  }
                  data-testid="adult-keywords-input"
                />
                <p className="text-xs text-muted-foreground">
                  Current: {settings.adult_keywords?.length || 0} keywords
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Settings */}
        <TabsContent value="rules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Retry Protection
              </CardTitle>
              <CardDescription>
                Configure call retry limits and blocking rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="retry-limit">Retry Limit</Label>
                  <Input
                    id="retry-limit"
                    type="number"
                    value={settings.retry_limit}
                    onChange={(e) =>
                      setSettings({ ...settings, retry_limit: parseInt(e.target.value) || 5 })
                    }
                    data-testid="retry-limit-input"
                  />
                  <p className="text-xs text-muted-foreground">Max retries within window</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retry-window">Retry Window (minutes)</Label>
                  <Input
                    id="retry-window"
                    type="number"
                    value={settings.retry_window_minutes}
                    onChange={(e) =>
                      setSettings({ ...settings, retry_window_minutes: parseInt(e.target.value) || 10 })
                    }
                    data-testid="retry-window-input"
                  />
                  <p className="text-xs text-muted-foreground">Time window for counting retries</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="block-duration">Block Duration (minutes)</Label>
                  <Input
                    id="block-duration"
                    type="number"
                    value={settings.block_duration_minutes}
                    onChange={(e) =>
                      setSettings({ ...settings, block_duration_minutes: parseInt(e.target.value) || 30 })
                    }
                    data-testid="block-duration-input"
                  />
                  <p className="text-xs text-muted-foreground">Temporary block duration</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Blocking Thresholds
              </CardTitle>
              <CardDescription>
                Configure automatic blocking rules for callers and publishers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adult-threshold">Adult Block Threshold</Label>
                  <Input
                    id="adult-threshold"
                    type="number"
                    value={settings.adult_block_threshold}
                    onChange={(e) =>
                      setSettings({ ...settings, adult_block_threshold: parseInt(e.target.value) || 2 })
                    }
                    data-testid="adult-threshold-input"
                  />
                  <p className="text-xs text-muted-foreground">Adult calls before auto-block</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spam-threshold">Spam Block Threshold</Label>
                  <Input
                    id="spam-threshold"
                    type="number"
                    value={settings.spam_block_threshold}
                    onChange={(e) =>
                      setSettings({ ...settings, spam_block_threshold: parseInt(e.target.value) || 3 })
                    }
                    data-testid="spam-threshold-input"
                  />
                  <p className="text-xs text-muted-foreground">Spam calls before auto-block</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="publisher-threshold">Publisher Pause Threshold</Label>
                  <Input
                    id="publisher-threshold"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={settings.publisher_pause_threshold}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        publisher_pause_threshold: parseFloat(e.target.value) || 0.3,
                      })
                    }
                    data-testid="publisher-threshold-input"
                  />
                  <p className="text-xs text-muted-foreground">Fraud score to auto-pause (0-1)</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="buffer-time">Buffer Time After IVR (seconds)</Label>
                <Input
                  id="buffer-time"
                  type="number"
                  value={settings.buffer_time_after_ivr}
                  onChange={(e) =>
                    setSettings({ ...settings, buffer_time_after_ivr: parseInt(e.target.value) || 150 })
                  }
                  className="max-w-xs"
                  data-testid="buffer-time-input"
                />
                <p className="text-xs text-muted-foreground">
                  Global minimum call duration requirement
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ringba Integration */}
        <TabsContent value="ringba" className="space-y-6">
          {/* Ringba Status Card */}
          {ringbaStatus && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${ringbaStatus.enabled ? 'bg-emerald-500/10' : 'bg-slate-500/10'}`}>
                      <Key className={`h-6 w-6 ${ringbaStatus.enabled ? 'text-emerald-500' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <p className="font-medium">Ringba Integration</p>
                      <p className="text-sm text-muted-foreground">
                        {ringbaStatus.enabled ? 'Enabled and syncing' : ringbaStatus.configured ? 'Configured but disabled' : 'Not configured'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    {ringbaStatus.enabled && (
                      <>
                        <div className="text-center">
                          <p className="text-emerald-500 font-semibold">{ringbaStatus.recent_syncs?.success || 0}</p>
                          <p className="text-xs text-muted-foreground">24h Success</p>
                        </div>
                        <div className="text-center">
                          <p className="text-red-500 font-semibold">{ringbaStatus.recent_syncs?.failed || 0}</p>
                          <p className="text-xs text-muted-foreground">24h Failed</p>
                        </div>
                      </>
                    )}
                    <Badge variant={ringbaStatus.enabled ? "default" : "secondary"} className={ringbaStatus.enabled ? "bg-emerald-500" : ""}>
                      {ringbaStatus.enabled ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                Ringba API Integration
              </CardTitle>
              <CardDescription>
                Configure Ringba API for real-time call tracking automation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Ringba Integration</p>
                  <p className="text-sm text-muted-foreground">
                    When enabled, actions will automatically sync with Ringba
                  </p>
                </div>
                <Switch
                  checked={settings.ringba_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, ringba_enabled: checked })
                  }
                  data-testid="ringba-enabled-switch"
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ringba-api-key">Ringba API Key</Label>
                  <Input
                    id="ringba-api-key"
                    type="password"
                    placeholder="Enter your Ringba API key"
                    value={settings.ringba_api_key || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, ringba_api_key: e.target.value })
                    }
                    data-testid="ringba-api-key-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ringba-account-id">Ringba Account ID</Label>
                  <Input
                    id="ringba-account-id"
                    placeholder="Enter your Ringba account ID"
                    value={settings.ringba_account_id || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, ringba_account_id: e.target.value })
                    }
                    data-testid="ringba-account-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ringba-webhook-url">Webhook URL</Label>
                  <Input
                    id="ringba-webhook-url"
                    placeholder="https://your-domain.com/api/webhooks/ringba"
                    value={settings.ringba_webhook_url || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, ringba_webhook_url: e.target.value })
                    }
                    data-testid="ringba-webhook-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Configure this URL in your Ringba account to receive webhook events
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ringba-webhook-secret">Webhook Secret</Label>
                  <Input
                    id="ringba-webhook-secret"
                    type="password"
                    placeholder="Enter webhook secret for verification"
                    value={settings.ringba_webhook_secret || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, ringba_webhook_secret: e.target.value })
                    }
                    data-testid="ringba-webhook-secret-input"
                  />
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-500 mb-2">Synchronized Actions</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Block / Unblock caller number</li>
                  <li>• Pause / Resume publisher</li>
                  <li>• Terminate call</li>
                  <li>• Update call status</li>
                  <li>• Fetch call recordings</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <Separator />
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !settings.ringba_api_key}
                  data-testid="test-connection-btn"
                >
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
                <Button
                  variant="outline"
                  onClick={handleImportTodaysCalls}
                  disabled={importing || !settings.ringba_enabled}
                  className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10"
                  data-testid="import-today-btn"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Fetch Today's Calls (Live)
                </Button>
                <Button
                  variant="outline"
                  onClick={handleImportCalls}
                  disabled={importing || !settings.ringba_enabled}
                  className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                  data-testid="import-calls-btn"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Import Historical (7 Days)
                </Button>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-500 mb-2">Live Call Mode</p>
                <p className="text-xs text-muted-foreground">
                  Click "Fetch Today's Calls" to retrieve only calls made today. Make a test call to your Ringba tracking number, then click the button to see it appear in Call Analysis.
                </p>
              </div>
              {!settings.ringba_enabled && settings.ringba_api_key && (
                <p className="text-xs text-amber-500">
                  Enable Ringba integration and save settings to import calls
                </p>
              )}
            </CardContent>
          </Card>

          {/* Auto-Import Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Scheduled Auto-Import (Today's Calls Only)
              </CardTitle>
              <CardDescription>
                Automatically fetch new calls from Ringba throughout the day
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Auto-Import</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically fetch today's new calls every {settings.auto_import_interval_minutes || 60} minutes
                  </p>
                </div>
                <Switch
                  checked={settings.auto_import_enabled || false}
                  onCheckedChange={(checked) => handleToggleAutoImport(checked)}
                  disabled={!settings.ringba_enabled || togglingAutoImport}
                  data-testid="auto-import-enabled-switch"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="auto-import-interval">Import Interval (minutes)</Label>
                  <Select
                    value={(settings.auto_import_interval_minutes || 60).toString()}
                    onValueChange={(value) =>
                      setSettings({ ...settings, auto_import_interval_minutes: parseInt(value) })
                    }
                    disabled={!settings.ringba_enabled}
                  >
                    <SelectTrigger data-testid="auto-import-interval-select">
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every hour</SelectItem>
                      <SelectItem value="120">Every 2 hours</SelectItem>
                      <SelectItem value="360">Every 6 hours</SelectItem>
                      <SelectItem value="720">Every 12 hours</SelectItem>
                      <SelectItem value="1440">Once a day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {autoImportStatus && (
                  <div className="space-y-2">
                    <Label>Scheduler Status</Label>
                    <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${autoImportStatus.scheduler_running ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className="text-sm">
                          {autoImportStatus.scheduler_running ? 'Scheduler Running' : 'Scheduler Stopped'}
                        </span>
                      </div>
                      {autoImportStatus.last_import && (
                        <p className="text-xs text-muted-foreground">
                          Last import: {new Date(autoImportStatus.last_import).toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })} EST
                        </p>
                      )}
                      {autoImportStatus.next_run && autoImportStatus.enabled && (
                        <p className="text-xs text-muted-foreground">
                          Next run: {new Date(autoImportStatus.next_run).toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })} EST
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleTriggerAutoImport}
                  disabled={!settings.ringba_enabled}
                  className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10"
                  data-testid="trigger-auto-import-btn"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Trigger Import Now
                </Button>
              </div>

              {!settings.ringba_enabled && (
                <p className="text-xs text-amber-500">
                  Enable Ringba integration to use auto-import
                </p>
              )}
            </CardContent>
          </Card>

          {/* Webhook Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Real-Time Webhooks
              </CardTitle>
              <CardDescription>
                Configure Ringba to send real-time call events to your webhook
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-500 mb-2">Your Webhook URL</p>
                <code className="text-sm bg-muted px-2 py-1 rounded block overflow-x-auto">
                  {window.location.origin}/api/webhooks/ringba
                </code>
              </div>
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong>Supported Events:</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>call_completed - Process completed calls in real-time</li>
                  <li>call_started - Detect blocked callers immediately</li>
                  <li>recording_available - Auto-download and analyze recordings</li>
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                Add this webhook URL in your Ringba account settings to receive real-time notifications when calls complete.
              </p>
            </CardContent>
          </Card>

          {/* Re-Analyze All Pending */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Re-Analyze Pending Calls
              </CardTitle>
              <CardDescription>
                Re-process all pending or failed calls through the AI pipeline (5 calls at a time in parallel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                This will find all calls with &quot;pending&quot; or &quot;failed&quot; status and re-run them through the AI analysis pipeline.
                Manually validated calls will be skipped.
              </div>
              <Button
                variant="outline"
                className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                data-testid="reanalyze-all-pending-btn"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/calls/reanalyze-all-pending`);
                    const data = res.data;
                    if (data.total === 0) {
                      toast.info("No pending or failed calls to re-analyze");
                    } else {
                      toast.success(`Re-analysis started for ${data.total} calls (${data.pending_count} pending, ${data.failed_count} failed)`);
                    }
                  } catch (e) {
                    toast.error("Failed to start re-analysis");
                  }
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-Analyze All Pending Calls
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Settings */}
        <TabsContent value="billing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Billing Configuration
              </CardTitle>
              <CardDescription>
                Users are billed per minute of call analyzed. 1 call minute = 1 minute deducted from their balance.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Rate Display */}
              <div className="bg-slate-950/60 border border-slate-800/40 rounded-xl p-4">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-3">Billing Model</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 font-mono font-bold text-lg">
                    1 min = 1 min
                  </span>
                  <span className="text-slate-500">deducted per call analyzed</span>
                </div>
                <p className="text-[10px] text-slate-600 mt-2">Example: A 5-minute call deducts 5 minutes from the user's balance</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800/40">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Low Balance Alert Threshold (minutes)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={settings.low_minutes_threshold ?? 100}
                    onChange={(e) => setSettings({ ...settings, low_minutes_threshold: parseInt(e.target.value) || 0 })}
                    className="bg-background/40 font-mono"
                    data-testid="low-credit-threshold"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Est. Usage per 5-min Call</Label>
                  <div className="flex items-center h-10 px-3 rounded-md border border-input bg-background/20 text-sm font-mono text-emerald-400">
                    5 minutes
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-slate-500">
                Minutes are deducted from user accounts after each successful call analysis. Admin accounts are not charged. Users with 0 minutes will have their analysis paused.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Log */}
        <TabsContent value="sync-log" className="space-y-6">
          {/* Sync Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{syncStats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-500">{syncStats.success}</p>
                <p className="text-xs text-muted-foreground">Success</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{syncStats.failed}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-500">{syncStats.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-500">{syncStats.skipped}</p>
                <p className="text-xs text-muted-foreground">Skipped</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="sync-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { fetchSyncLogs(); fetchSyncData(); }}
                    data-testid="refresh-sync-logs"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  {syncStats.failed > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleRetryFailed}
                      disabled={retrying}
                      className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                      data-testid="retry-failed-btn"
                    >
                      {retrying ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Retry Failed ({syncStats.failed})
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={handlePurgeBadLogs}
                    disabled={purging}
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                    data-testid="purge-bad-logs-btn"
                    title="Purge sync_logs that match known bad-state patterns (not-configured errors, Atlas timeouts, stale empty entries)"
                  >
                    {purging ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Purge Bad Logs
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleFactoryReset}
                    disabled={resetting}
                    className="text-fuchsia-400 border-fuchsia-500/40 hover:bg-fuchsia-500/10"
                    data-testid="factory-reset-btn"
                    title="Nuclear reset: wipes import tracking, restarts scheduler & bg-loop, forces fresh 7-day fetch from Ringba"
                  >
                    {resetting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Factory Reset Import
                  </Button>
                </div>
              </div>
              {/* Purge Number by phone — permanently blocks a specific caller in local DB even if Ringba sync fails */}
              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-border/50" data-testid="purge-number-row">
                <div className="text-xs text-muted-foreground shrink-0 mr-1">
                  Number stuck in a loop?
                </div>
                <Input
                  type="tel"
                  placeholder="+13124024711"
                  value={purgeNumberInput}
                  onChange={(e) => setPurgeNumberInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handlePurgeNumber(); }}
                  className="max-w-[220px] h-9"
                  data-testid="purge-number-input"
                />
                <Button
                  variant="outline"
                  onClick={handlePurgeNumber}
                  disabled={purgingNumber}
                  className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                  data-testid="purge-number-btn"
                  title="Permanently block this number in local DB — deletes all its sync_logs & import_attempts. Future calls silently ignored."
                >
                  {purgingNumber ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Ban className="h-4 w-4 mr-2" />
                  )}
                  Purge Number
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sync Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Synchronization Log
              </CardTitle>
              <CardDescription>
                Track all Ringba API synchronization actions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Object</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Response / Error</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : syncLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        <div className="flex flex-col items-center gap-2">
                          <Activity className="h-8 w-8 text-muted-foreground/50" />
                          <p>No sync logs yet. Actions will appear here when Ringba integration is enabled.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    syncLogs.map((log) => {
                      const config = statusConfig[log.status] || statusConfig.pending;
                      const StatusIcon = config.icon;
                      return (
                        <TableRow key={log.id} data-testid={`sync-log-${log.id}`}>
                          <TableCell>
                            <Badge variant="outline" className={config.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium capitalize">
                              {log.action_type?.replace(/_/g, ' ')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-mono text-xs">{log.object_id}</p>
                              <p className="text-xs text-muted-foreground">{log.object_type}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })} EST
                          </TableCell>
                          <TableCell className="max-w-xs">
                            {log.error_message ? (
                              <p className="text-xs text-red-500 truncate" title={log.error_message}>
                                {log.error_message}
                              </p>
                            ) : log.ringba_response ? (
                              <p className="text-xs text-emerald-500">
                                Status: {log.ringba_response.status_code}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">-</p>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {log.status === "pending" || log.status === "failed" ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStopLog(log.id)}
                                data-testid={`stop-log-${log.id}`}
                                title="Stop retrying forever"
                              >
                                <Ban className="h-4 w-4 text-muted-foreground hover:text-orange-500" />
                              </Button>
                            ) : log.paused_by_user ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResumeLog(log.id)}
                                data-testid={`resume-log-${log.id}`}
                                title="Resume retries"
                              >
                                <Play className="h-4 w-4 text-muted-foreground hover:text-emerald-500" />
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteLog(log.id)}
                              data-testid={`delete-log-${log.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Account Settings */}
        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your admin account password
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 chars, A-z, 0-9, !@#)"
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>

              <Separator />

              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-2">Password Requirements:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Minimum 6 characters</li>
                  <li>Recommended: mix of letters, numbers, and symbols</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Global confirm dialog — replaces window.confirm() which Safari can silently block */}
      <AlertDialog
        open={!!confirmDialog}
        onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}
      >
        <AlertDialogContent data-testid="settings-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="settings-confirm-ok"
              className={confirmDialog?.confirmVariant === "destructive" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              onClick={async () => {
                const fn = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (fn) await fn();
              }}
            >
              {confirmDialog?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
